import {
    Box,
    Grid,
    Link as MuiLink,
    Stepper,
    Typography,
    Button,
    Stack,
    Alert,
    Tooltip,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router";
import { Link, useSearchParams } from "react-router-dom";
import validator from "validator";
import clsx from "clsx";
import { AcxStep } from "components/UI/AcxStep";
import { useStore } from "utils/useStore";
import {
    LLMClassifierBuilderStore,
    LOADED_VERSION_SEARCH_PARAM,
} from "../Stores/LLMClassifierBuilderStore";
import {
    ArrowRightAlt,
    ContentCopyRounded,
    EditRounded,
    HistoryRounded,
    MoreVert,
    SyncRounded,
    UnpublishedRounded,
} from "@mui/icons-material";
import { LLMClassifierBuilderStepperStore } from "../Stores/LLMClassifierBuilderStepperStore";
import { observer } from "mobx-react";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { PageToolbar } from "../../Common/PageToolbar";
import { LLMClassifiersStore } from "../Stores/LLMClassifiersStore";
import { useUnpublishConfirmation } from "components/Classifiers/Common/UnpublishButton";
import { usePublishConfirmation } from "../Components/usePublishConfirmation";
import moment from "moment";
import { ModelPublishDialog } from "../Components/ModelPublishDialog";
import { UpdateFromLibraryDialog } from "../Components/UpdateFromLibraryDialog";
import { AcxMenuItem, AcxMenuV2 } from "components/UI/Menu/AcxMenu";
import {
    isLLMClassifierVersionPublished,
    llmClassifierVersionLabel,
} from "../utils/llmClassifierVersionDisplay";
import { contextAlertSx } from "../utils/contextAlert";
import { AcxExpandableText } from "components/UI/AcxExpandableText";
import {
    JustSavedInfo,
    SaveFlowContext,
    useCreateSaveFlow,
    useSaveFlow,
} from "../Components/SaveFlow";
import { useUnsavedChangesNavigationGuard } from "utils/useUnsavedChangesNavigationGuard";
import { UnsavedChangesDialog } from "../Components/UnsavedChangesDialog";
import {
    VersionSwitchContext,
    useCreateVersionSwitch,
} from "../Components/VersionSwitch";
import {
    RestoreRounded,
    CheckCircleRounded,
    InfoRounded,
} from "@mui/icons-material";

const newLLMClassifierKey = "new";

function isValidId(id: string | undefined): id is string {
    return !!id && (id === newLLMClassifierKey || validator.isUUID(id));
}

function isNew(id: string | undefined): boolean {
    return id === newLLMClassifierKey;
}

/** `steps` is a const tuple, so only opted-in steps carry the flag. */
function stepFillsHeight(step: { name: string }) {
    return "fillsHeight" in step && step.fillsHeight === true;
}

function VersionLabel({ children }: { children: string }) {
    return (
        <Box component="span" fontWeight="bold" color="black.main">
            {children}
        </Box>
    );
}

interface VersionBannerProps {
    severity: "success" | "info";
    icon: ReactNode;
    action: ReactNode;
    children: ReactNode;
}

function VersionBanner({
    severity,
    icon,
    action,
    children,
}: VersionBannerProps) {
    return (
        <Grid item>
            <Alert
                severity={severity}
                icon={icon}
                sx={(theme) => ({
                    alignItems: "center",
                    ...contextAlertSx(theme, severity),
                    pr: 3,
                })}
                action={action}
            >
                <Typography variant="body1">{children}</Typography>
            </Alert>
        </Grid>
    );
}

function VersionBannerAction({
    children,
    onClick,
}: {
    children: ReactNode;
    onClick: () => void;
}) {
    return (
        <MuiLink
            component="button"
            type="button"
            sx={(theme) => ({
                whiteSpace: "nowrap",
                fontWeight: "bold",
                fontSize: theme.typography.body1.fontSize,
            })}
            onClick={onClick}
        >
            {children}
        </MuiLink>
    );
}

interface JustSavedVersionBannerContentProps {
    justSaved: JustSavedInfo;
    untouchedPublishedLabel?: string;
}

function JustSavedVersionBannerContent({
    justSaved: { newVersionLabel, previousLabel },
    untouchedPublishedLabel,
}: JustSavedVersionBannerContentProps) {
    if (!previousLabel)
        return (
            <>
                {newVersionLabel && (
                    <VersionLabel>{newVersionLabel}</VersionLabel>
                )}{" "}
                created.
            </>
        );

    const unchangedVersionsContent = untouchedPublishedLabel ? (
        <>
            <VersionLabel>{previousLabel}</VersionLabel> and the published
            version, <VersionLabel>{untouchedPublishedLabel}</VersionLabel>, are
            both untouched in version history.
        </>
    ) : (
        <>
            <VersionLabel>{previousLabel}</VersionLabel> is untouched in version
            history.
        </>
    );

    return (
        <>
            {newVersionLabel && <VersionLabel>{newVersionLabel}</VersionLabel>}{" "}
            created from <VersionLabel>{previousLabel}</VersionLabel>.{" "}
            {unchangedVersionsContent}
        </>
    );
}

interface OlderDraftBannerContentProps {
    editingVersionLabel: string;
    newestVersionLabel: string;
}

function OlderDraftBannerContent({
    editingVersionLabel,
    newestVersionLabel,
}: OlderDraftBannerContentProps) {
    return (
        <>
            You are viewing <VersionLabel>{editingVersionLabel}</VersionLabel>,
            an older draft. <VersionLabel>{newestVersionLabel}</VersionLabel> is
            the newest. Save Model creates a new version from it.{" "}
            <VersionLabel>{editingVersionLabel}</VersionLabel> is never
            overwritten.
        </>
    );
}

interface EditingVersionBannerContentProps {
    editingVersionLabel: string;
    branchedFromLabel?: string;
    publishedLabel?: string;
}

function EditingVersionBannerContent({
    editingVersionLabel,
    branchedFromLabel,
    publishedLabel,
}: EditingVersionBannerContentProps) {
    return (
        <>
            Editing <VersionLabel>{editingVersionLabel}</VersionLabel>
            {branchedFromLabel && (
                <>
                    , branched from{" "}
                    <VersionLabel>{branchedFromLabel}</VersionLabel>
                </>
            )}
            {publishedLabel && (
                <>
                    {". "}
                    <VersionLabel>{publishedLabel}</VersionLabel> is published
                </>
            )}
        </>
    );
}

export const LLMClassifierBuilderLayout = observer(function () {
    const builderStore = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const saveFlow = useCreateSaveFlow();
    const navigationGuard = useUnsavedChangesNavigationGuard({
        hasUnsavedChanges: () => builderStore.hasUnsavedChanges(),
        triggerSave: saveFlow.triggerSave,
    });

    // Leaving the editor (or opening another model) ends its test; switching wizard steps does not.
    useEffect(() => () => builderStore.cancelTestLLMClassifier(), [builderStore, id]);

    // Read once on open: the store writes it with replaceState, so our own switches never re-enter here.
    const versionParam =
        searchParams.get(LOADED_VERSION_SEARCH_PARAM) ?? undefined;
    useEffect(() => {
        if (!isValidId(id)) return;

        if (isNew(id)) {
            builderStore.reset();
        } else {
            builderStore.editLLMClassifier(id, versionParam);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, builderStore]);

    const stepPath = searchParams.get("step");
    useEffect(() => {
        stepper.resetHasError();

        if (!LLMClassifierBuilderStepperStore.isValidWizardStepPath(stepPath))
            return stepper.setCurrentWizardStep(
                LLMClassifierBuilderStepperStore.DefaultWizardStep,
                true,
            );

        if (stepPath === stepper.currentWizardStep.path) return;

        if (
            isNew(id) &&
            stepPath !== LLMClassifierBuilderStepperStore.DefaultWizardStep.path
        ) {
            stepper.setCurrentWizardStep(
                LLMClassifierBuilderStepperStore.DefaultWizardStep,
                true,
            );
            return;
        }

        // If the query param and the store state are different, update the store state to match the query param
        stepper.setCurrentWizardStep(
            LLMClassifierBuilderStepperStore.getWizardStepFromPath(stepPath),
            true,
        );
    }, [stepper, stepPath, id]);

    if (!isValidId(id)) return <InvalidURLNotifier />;

    const isLoading = builderStore.getTaskLoading(
        LLMClassifierBuilderStore.Tasks.LoadEditLLMClassifier,
    );
    const fillsHeight = stepFillsHeight(stepper.currentWizardStep);

    return (
        <SaveFlowContext.Provider value={saveFlow}>
            <Grid
                width="100%"
                // Only step that fills the height needs a definite height passed down.
                {...(fillsHeight
                    ? {
                          height: "100%",
                          minHeight: 0,
                          display: "flex",
                          flexDirection: "column",
                      }
                    : {})}
            >
                <ModelBuilderToolbar />
                {!isLoading && <LLMClassifierBuilderWizard />}
                {isLoading && (
                    <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                    >
                        <AcxEmptyState size="large" variant="loading" />
                    </Grid>
                )}
            </Grid>
            {saveFlow.dialogs}
            <UnsavedChangesDialog
                isOpen={navigationGuard.isBlocked}
                onClose={navigationGuard.onCancel}
                onDiscard={navigationGuard.onDiscard}
                onSaveFirst={navigationGuard.onSaveFirst}
            />
        </SaveFlowContext.Provider>
    );
});

const LLMClassifierBuilderWizard = observer(function () {
    const builderStore = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);
    const currentStep = stepper.currentWizardStep;
    const { justSaved, dismissJustSaved } = useSaveFlow();
    const versionSwitch = useCreateVersionSwitch();

    const [showMlAlert, setShowMlAlert] = useState(true);

    useEffect(() => {
        if (builderStore.isSaved) void builderStore.loadLLMClassifierVersions();
    }, [builderStore, builderStore.isSaved]);

    const loadedImplementation = builderStore.classifier.implementation;

    // The just-saved confirmation only describes the version it was created for; moving on from
    // it (loading a different version) makes it stale, so the next relevant banner takes over.
    useEffect(() => {
        if (justSaved && loadedImplementation.id !== justSaved.newVersionId) {
            dismissJustSaved();
        }
    }, [justSaved, loadedImplementation.id, dismissJustSaved]);

    const editingVersionLabel = llmClassifierVersionLabel(
        loadedImplementation.createdOn,
    );
    const publishedVersion = builderStore.llmClassifierVersions.find(
        (version) =>
            isLLMClassifierVersionPublished(
                builderStore.classifier.isPublished,
                version,
            ),
    );
    const publishedLabel = llmClassifierVersionLabel(
        publishedVersion?.createdOn,
    );
    const loadedVersion = builderStore.llmClassifierVersions.find(
        (version) => version.id === loadedImplementation.id,
    );
    const branchedFromVersion = builderStore.llmClassifierVersions.find(
        (version) => version.id === loadedVersion?.branchedFromImplementationId,
    );
    const branchedFromLabel = llmClassifierVersionLabel(
        branchedFromVersion?.createdOn,
    );
    const newestVersion = builderStore.llmClassifierVersions[0];
    const isNewestVersion =
        !newestVersion || newestVersion.id === loadedImplementation.id;
    const isLoadedVersionPublished = isLLMClassifierVersionPublished(
        builderStore.classifier.isPublished,
        loadedImplementation,
    );
    const unsavedChanges =
        builderStore.getSaveChanges()?.sections.flatMap((s) => s.changes) ?? [];
    const unsavedChangesBannerContent =
        builderStore.isSaved &&
        editingVersionLabel &&
        unsavedChanges.length > 0 ? (
            <>
                {unsavedChanges.length} unsaved{" "}
                {unsavedChanges.length === 1 ? "edit" : "edits"} on{" "}
                <VersionLabel>{editingVersionLabel}</VersionLabel>:{" "}
                {unsavedChanges.join(", ")}. Saving creates a new version
                branching from it.{" "}
                <VersionLabel>{editingVersionLabel}</VersionLabel> is never
                overwritten.
            </>
        ) : builderStore.hasUnsavedChanges() ? (
            <>
                You have unsaved edits.{" "}
                {builderStore.isSaved
                    ? "Save your changes to create a new version."
                    : "Save to create this model’s first version."}
            </>
        ) : undefined;

    // Only one state banner shows at a time; unsaved edits are the most pressing thing to
    // surface, so they take over from whichever version-context banner would otherwise show.
    // The first sentence names the branch source, so only the published version needs a label.
    const untouchedPublishedLabel =
        publishedLabel &&
        publishedLabel !== justSaved?.previousLabel &&
        publishedLabel !== justSaved?.newVersionLabel
            ? publishedLabel
            : undefined;
    const isViewingJustSavedVersion =
        justSaved?.newVersionId === loadedImplementation.id;
    const justSavedBannerContent =
        !unsavedChangesBannerContent &&
        justSaved &&
        isViewingJustSavedVersion ? (
            <JustSavedVersionBannerContent
                justSaved={justSaved}
                untouchedPublishedLabel={untouchedPublishedLabel}
            />
        ) : undefined;

    // Not published and not the newest version: an older draft, which needs a more pointed
    // warning than the general editing-version banner — you may be about to build on stale content.
    const shouldShowOlderDraftBanner =
        !unsavedChangesBannerContent &&
        !justSavedBannerContent &&
        !!editingVersionLabel &&
        !!newestVersion &&
        !isNewestVersion &&
        !isLoadedVersionPublished;
    const olderDraftBannerContent = shouldShowOlderDraftBanner ? (
        <OlderDraftBannerContent
            editingVersionLabel={editingVersionLabel!}
            newestVersionLabel={
                llmClassifierVersionLabel(newestVersion!.createdOn) ?? ""
            }
        />
    ) : undefined;

    // Already viewing the published version, so there's nothing useful to orient the user with.
    const shouldShowEditingVersionBanner =
        !unsavedChangesBannerContent &&
        !justSavedBannerContent &&
        !shouldShowOlderDraftBanner &&
        !!editingVersionLabel &&
        !isLoadedVersionPublished;
    const editingVersionBannerContent = shouldShowEditingVersionBanner ? (
        <EditingVersionBannerContent
            editingVersionLabel={editingVersionLabel!}
            branchedFromLabel={branchedFromLabel}
            publishedLabel={publishedLabel}
        />
    ) : undefined;

    const stepIndex = LLMClassifierBuilderStepperStore.WizardSteps.findIndex(
        (s) => s.path === currentStep.path,
    );
    const previousStep =
        LLMClassifierBuilderStepperStore.WizardSteps[stepIndex - 1];
    const nextStep =
        LLMClassifierBuilderStepperStore.WizardSteps[stepIndex + 1];

    const Component = currentStep.component;
    const fillsHeight = stepFillsHeight(currentStep);

    return (
        <VersionSwitchContext.Provider value={versionSwitch}>
            <Grid
                container
                direction="column"
                px={{ xs: 2, md: 8 }}
                py={4}
                rowGap={3}
                {...(fillsHeight
                    ? { flex: 1, minHeight: 0, overflow: "auto" }
                    : {})}
            >
                <Grid container item justifyContent="space-between">
                    <Grid item width="fit-content">
                        <Stepper activeStep={stepIndex}>
                            {LLMClassifierBuilderStepperStore.WizardSteps.map(
                                (step, idx) => {
                                    return (
                                        <AcxStep
                                            key={step.name}
                                            className={clsx({
                                                first: idx === 0,
                                                last:
                                                    idx ===
                                                    LLMClassifierBuilderStepperStore
                                                        .WizardSteps.length -
                                                        1,
                                                active:
                                                    step.path ===
                                                    currentStep.path,
                                                inactive:
                                                    step.path !==
                                                    currentStep.path,
                                            })}
                                        >
                                            <Button
                                                onClick={() =>
                                                    stepper.setCurrentWizardStep(
                                                        step,
                                                    )
                                                }
                                            >
                                                {step.name}
                                            </Button>
                                        </AcxStep>
                                    );
                                },
                            )}
                        </Stepper>
                    </Grid>
                </Grid>
                {justSavedBannerContent && (
                    <VersionBanner
                        severity="success"
                        icon={
                            <CheckCircleRounded
                                fontSize="small"
                                sx={{ color: "success.main" }}
                            />
                        }
                        action={
                            <VersionBannerAction
                                onClick={() =>
                                    stepper.setCurrentWizardStep(
                                        "approvals",
                                        true,
                                    )
                                }
                            >
                                Version history
                            </VersionBannerAction>
                        }
                    >
                        {justSavedBannerContent}
                    </VersionBanner>
                )}
                {olderDraftBannerContent && (
                    <VersionBanner
                        severity="info"
                        icon={
                            <RestoreRounded
                                fontSize="small"
                                sx={{ color: "primary.main" }}
                            />
                        }
                        action={
                            <VersionBannerAction
                                onClick={() => {
                                    if (newestVersion)
                                        versionSwitch.selectVersion(
                                            newestVersion.id,
                                        );
                                }}
                            >
                                Back to{" "}
                                {llmClassifierVersionLabel(
                                    newestVersion?.createdOn,
                                )}
                            </VersionBannerAction>
                        }
                    >
                        {olderDraftBannerContent}
                    </VersionBanner>
                )}
                {editingVersionBannerContent && (
                    <VersionBanner
                        severity="info"
                        icon={
                            <InfoRounded
                                fontSize="small"
                                sx={{ color: "primary.main" }}
                            />
                        }
                        action={
                            <VersionBannerAction
                                onClick={() =>
                                    stepper.setCurrentWizardStep(
                                        "approvals",
                                        true,
                                    )
                                }
                            >
                                Version history
                            </VersionBannerAction>
                        }
                    >
                        {editingVersionBannerContent}
                    </VersionBanner>
                )}
                {unsavedChangesBannerContent && (
                    <Grid item>
                        <Alert
                            severity="warning"
                            icon={
                                <EditRounded
                                    fontSize="small"
                                    sx={{ color: "yellow.dark" }}
                                />
                            }
                            sx={(theme) => ({
                                border: `1px solid ${theme.palette.yellow.main}`,
                                backgroundColor:
                                    theme.palette.yellow.background,
                                color: theme.palette.gray[800],
                                "& .MuiAlert-message": {
                                    minWidth: 0,
                                    overflowWrap: "anywhere",
                                },
                            })}
                        >
                            <AcxExpandableText
                                variant="body1"
                                maxTruncatedLines={2}
                            >
                                {unsavedChangesBannerContent}
                            </AcxExpandableText>
                        </Alert>
                    </Grid>
                )}
                {builderStore.publishBlockedReason && (
                    <Grid item>
                        {/* Publish is disabled if an LLM model is referenced. */}
                        <Alert severity="info">
                            {builderStore.publishBlockedReason}
                        </Alert>
                    </Grid>
                )}
                {showMlAlert && builderStore.mlModelWarningMessage && (
                    <Grid item>
                        <Alert
                            severity="warning"
                            onClose={() => setShowMlAlert(false)}
                        >
                            {builderStore.mlModelWarningMessage}
                        </Alert>
                    </Grid>
                )}
                <Grid
                    item
                    {...(fillsHeight
                        ? { display: "flex", flex: 1, minHeight: 0 }
                        : {})}
                >
                    <Component />
                </Grid>
                <Grid
                    container
                    item
                    alignSelf="flex-end"
                    alignItems="center"
                    direction="row"
                    columnGap={2}
                    width="fit-content"
                >
                    {previousStep && (
                        <AcxButton
                            color="white"
                            onClick={() =>
                                stepper.setCurrentWizardStep(previousStep)
                            }
                        >
                            Back
                        </AcxButton>
                    )}
                    {nextStep && (
                        <AcxButton
                            onClick={() =>
                                stepper.setCurrentWizardStep(nextStep)
                            }
                        >
                            Next{" "}
                            <ArrowRightAlt
                                sx={(theme) => ({ ml: theme.spacing(1) })}
                            />
                        </AcxButton>
                    )}
                </Grid>
            </Grid>
            {versionSwitch.dialog}
        </VersionSwitchContext.Provider>
    );
});

const ModelNameInformation = observer(function () {
    const store = useStore(LLMClassifierBuilderStore);
    const name = store.classifier.name;

    if (!name) return null;

    return (
        <Typography
            color="gray.900"
            fontWeight={600}
            noWrap
            title={name}
            sx={{ maxWidth: { xs: "12rem", lg: "20rem" } }}
        >
            {name}
        </Typography>
    );
});

const LastUpdatedInformation = observer(function () {
    const store = useStore(LLMClassifierBuilderStore);

    // The label already carries the version's timestamp, so this reports when the model last changed.
    const versionLabel = llmClassifierVersionLabel(
        store.classifier.implementation.createdOn,
    );
    const lastUpdatedOn =
        store.classifier.modifiedOn ?? store.classifier.createdOn;

    if (!store.isSaved) return null;

    return (
        <Typography color="gray.600" variant="body2" whiteSpace="nowrap" mr={1}>
            {versionLabel}
            {versionLabel && lastUpdatedOn && " · "}
            {lastUpdatedOn &&
                `Last Updated ${moment
                    .utc(lastUpdatedOn)
                    .local()
                    .format("HH:mm MM/DD/yyyy")}`}
        </Typography>
    );
});

const ModelBuilderToolbar = observer(function () {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);
    const llmClassifiersStore = useStore(LLMClassifiersStore);
    const { triggerSave, isSaving } = useSaveFlow();

    const publish = usePublishConfirmation({
        onConfirmPublish: async () => {
            const result = await store.publishLLMClassifier();
            if (!result?.dashboardId) return;

            setModelPublishDialogInfo({
                dashboardId: result.dashboardId,
                dashboardTitle: result.dashboardTitle ?? undefined,
            });
            setIsModelPublishDialogOpen(true);
        },
    });
    const unpublish = useUnpublishConfirmation({
        classifierWording: "model",
        onConfirmUnpublish: async () => {
            await store.unpublishLLMClassifier();
        },
    });

    const [isUpdateFromLibraryOpen, setIsUpdateFromLibraryOpen] =
        useState(false);
    const [isModelPublishDialogOpen, setIsModelPublishDialogOpen] =
        useState(false);
    const [modelPublishDialogInfo, setModelPublishDialogInfo] = useState<{
        dashboardTitle?: string;
        dashboardId: string;
    }>({ dashboardId: "" });
    const overflowMenuAnchorRef = useRef<HTMLElement | null>(null);
    const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

    const isPublishLoading = llmClassifiersStore.getTaskLoading(
        LLMClassifiersStore.Tasks.PublishLLMClassifier,
    );
    const isCopyLoading = llmClassifiersStore.getTaskLoading(
        LLMClassifiersStore.Tasks.CopyLLMClassifier,
    );
    const isAnyLoading =
        isSaving || isPublishLoading || unpublish.isLoading || isCopyLoading;
    const isPublished = store.classifier.isPublished === true;
    // Publish promotes the version in the editor, so it is offered for any version that is not deployed.
    const isLoadedVersionLive = isLLMClassifierVersionPublished(
        store.classifier.isPublished,
        store.classifier.implementation,
    );
    // Publish deploys the stored snapshot, not the editor, so pending edits must be saved first. An
    // absent diff means dirtiness is unknown, which fails closed rather than publishing over edits.
    const mayHaveUnsavedChanges = store.hasUnsavedChanges();
    const publishDisabledReason = isLoadedVersionLive
        ? "This version is already published."
        : store.publishBlockedReason ??
          (mayHaveUnsavedChanges
              ? "Save your changes before publishing."
              : undefined);

    return (
        <PageToolbar
            title="Model Builder"
            sx={{ position: "sticky", top: 0, zIndex: 100 }}
        >
            <Stack
                direction="row"
                columnGap={1}
                width="fit-content"
                alignItems="center"
            >
                <ModelNameInformation />
                <LastUpdatedInformation />
                <Tooltip title={publishDisabledReason ?? ""}>
                    <span>
                        <AcxButton
                            disabled={
                                isAnyLoading ||
                                !store.isSaved ||
                                publishDisabledReason !== undefined
                            }
                            loading={isPublishLoading}
                            onClick={publish.openConfirmation}
                        >
                            Publish
                        </AcxButton>
                    </span>
                </Tooltip>
                <AcxButton
                    onClick={() => triggerSave()}
                    disabled={
                        !store.isLLMClassifierSavable() ||
                        isPublishLoading ||
                        unpublish.isLoading ||
                        isCopyLoading
                    }
                    loading={isSaving}
                >
                    Save Model
                </AcxButton>
                <AcxButton
                    ref={overflowMenuAnchorRef}
                    onClick={() => setIsOverflowMenuOpen((open) => !open)}
                    color="white"
                    disabled={isAnyLoading}
                    containerStyle={{ width: "30px", height: "30px" }}
                    sx={(theme) => ({
                        width: "30px",
                        height: "30px",
                        minWidth: "0px",
                        fontSize: "16px",
                        padding: theme.spacing(1),
                    })}
                >
                    <MoreVert />
                </AcxButton>
                <AcxMenuV2
                    anchorEl={overflowMenuAnchorRef.current}
                    open={isOverflowMenuOpen}
                    onClose={() => setIsOverflowMenuOpen(false)}
                >
                    {store.isSaved && (
                        <AcxMenuItem
                            icon={
                                <HistoryRounded
                                    fontSize="small"
                                    color="inherit"
                                    sx={{ color: "gray.600" }}
                                />
                            }
                            onClick={() => {
                                stepper.setCurrentWizardStep("approvals", true);
                                setIsOverflowMenuOpen(false);
                            }}
                        >
                            Version History
                        </AcxMenuItem>
                    )}
                    {store.isSaved && store.isFromLibrary && (
                        <AcxMenuItem
                            icon={
                                <SyncRounded
                                    fontSize="small"
                                    color="inherit"
                                    sx={{ color: "gray.600" }}
                                />
                            }
                            onClick={() => {
                                setIsUpdateFromLibraryOpen(true);
                                setIsOverflowMenuOpen(false);
                            }}
                        >
                            Update from Library
                        </AcxMenuItem>
                    )}
                    <Tooltip title="Immediately creates a new model with the same configuration as this one and navigates you there.">
                        <AcxMenuItem
                            icon={
                                <ContentCopyRounded
                                    fontSize="small"
                                    color="inherit"
                                    sx={{ color: "gray.600" }}
                                />
                            }
                            disabled={
                                isSaving ||
                                !store.isSaved ||
                                isPublishLoading ||
                                unpublish.isLoading ||
                                // Disabling copy for logic chain classifiers for now. There needs to be
                                // some logic for updating the classifier id to the copied classifier id
                                // on actions that write to classifier results.
                                // TODO: come back to this
                                store.classifier.implementation.isLogicChain()
                            }
                            onClick={() => store.copyLLMClassifier()}
                        >
                            Copy
                        </AcxMenuItem>
                    </Tooltip>
                    {isPublished && (
                        <AcxMenuItem
                            icon={
                                <UnpublishedRounded
                                    fontSize="small"
                                    color="inherit"
                                    sx={{ color: "gray.600" }}
                                />
                            }
                            disabled={
                                isSaving ||
                                unpublish.isLoading ||
                                isPublishLoading ||
                                isCopyLoading ||
                                !store.isSaved
                            }
                            onClick={unpublish.openConfirmation}
                        >
                            Unpublish
                        </AcxMenuItem>
                    )}
                </AcxMenuV2>
                <ModelPublishDialog
                    isOpen={isModelPublishDialogOpen}
                    setIsOpen={setIsModelPublishDialogOpen}
                    modelPublishDialogInfo={modelPublishDialogInfo}
                />
                <UpdateFromLibraryDialog
                    isOpen={isUpdateFromLibraryOpen}
                    onClose={() => setIsUpdateFromLibraryOpen(false)}
                />
                {publish.confirmationDialog}
                {unpublish.confirmationDialog}
            </Stack>
        </PageToolbar>
    );
});

function InvalidURLNotifier() {
    return (
        <Grid
            container
            direction="column"
            width="100%"
            height="100%"
            justifyContent="center"
            alignItems="center"
            rowGap={2}
        >
            <Grid item>
                <Typography
                    sx={(theme) => ({
                        color: theme.palette.gray[700],
                        fontSize: "28px",
                        fontWeight: "bold",
                    })}
                >
                    Page not found
                </Typography>
            </Grid>
            <Grid item>
                <Link
                    className="pendo-ignore"
                    to="/app/classifiers/llm"
                    style={{ textDecoration: "none" }}
                >
                    <AcxButton color="white">Return to Your Models</AcxButton>
                </Link>
            </Grid>
        </Grid>
    );
}
